import { useTranslation } from "react-i18next";
import { FunnelStepContainer } from "v2/components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepBackControl } from "v2/components/funnel/ui-elements/FunnelStepControls";
import { FunnelStepSection } from "v2/components/funnel/ui-elements/FunnelStepSection";
import { z } from "zod";
import { FunnelStepControls } from "v2/components/funnel/ui-elements/FunnelStepControls";
import { FunnelStepForwardControl } from "v2/components/funnel/ui-elements/FunnelStepControls";
import { useFormHandlers, useForm, ControlledRadioGroup } from "v2/modules/form";
import { Box, Flex } from "@chakra-ui/react";
import {
  selectDisruptedFlightByIndex,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { airportSchema } from "./FlightDetails";
import { useFunnel } from "@airhelp/funnel";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { AirplaneIcon } from "v2/components/funnel/ui-elements/airplane-icon/Airplane";
import { isFirstStep } from "../state/appState";

export const airlineSchema = z.object({
  code: z.string().optional(),
  name: z.string(),
  ec261: z.boolean().optional(),
  displayName: z.string().optional(),
  countryCode: z.string().optional(),
  iata: z.string().nullable(),
  icao: z.string().nullable().optional(),
  uniqueIdentifier: z.string(),
});

const itineraryFlightSchema = z.object({
  disrupted: z.boolean(),
  flightNumber: z.string().nullable(),
  flightDate: z.date().nullable(),
  departureAirport: airportSchema.nullable(),
  arrivalAirport: airportSchema.nullable(),
  airline: airlineSchema.nullable(),
});

const defaultStepSchema = z.object({
  disruptedFlight: itineraryFlightSchema.nullable(),
});

const stepSchema = z.object({
  disruptedFlight: itineraryFlightSchema,
});

export function MultilegDisruptedFlightStep() {
  const { t } = useTranslation();
  const { itineraryFlights } = usePreEligibilityDataSnapshot();
  const funnel = useFunnel();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      disruptedFlight:
        itineraryFlights.find((itineraryFlight) => itineraryFlight.disrupted) || null,
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    onSubmit({ disruptedFlight }) {
      const disruptedFlightIndex = itineraryFlights.findIndex((itineraryFlight) => {
        return itineraryFlight.arrivalAirport?.iata === disruptedFlight?.arrivalAirport?.iata;
      });

      selectDisruptedFlightByIndex(disruptedFlightIndex);

      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      funnel.stepForward();
    },
  });
  return (
    <FunnelStepContainer data-testid="itineraryDetailsStep">
      <FunnelStepSection title={t("steps.multileg_disrupted_flight.title")}>
        <ControlledRadioGroup
          name={"disruptedFlight"}
          control={control}
          options={itineraryFlights}
          renderOption={(option) => (
            <Flex align="center">
              <Box
                flexGrow={{ base: 1, sm: 0 }}
              >{`${option.departureAirport?.city} (${option.departureAirport?.iata})`}</Box>
              <AirplaneIcon color="greyscale.500" my={0} mx={2} boxSize={6} />
              <Box
                textAlign={{ base: "right", sm: "initial" }}
                flexGrow={{ base: 1, sm: 0 }}
              >{`${option.arrivalAirport?.city} (${option.arrivalAirport?.iata})`}</Box>
            </Flex>
          )}
        />
      </FunnelStepSection>
      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        {!isFirstStep(funnel.currentStep?.uri) && <FunnelStepBackControl />}

        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
